'use client';
import React, { useRef } from 'react';
import { TypeBlockScrolljackSection, TypeScrolljackItem } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import {
    AnimatePresence,
    LazyMotion,
    m as motion,
    useScroll,
    useTransform,
    useMotionValueEvent,
    useReducedMotion,
} from 'framer-motion';
import TextMarkdown from '../text-markdown/text-markdown';
import classNames from 'classnames';

const loadFeatures = () => import('../__constants__/framer-features').then((res) => res.default);

const MIN_ITEMS = 2;
const MAX_ITEMS = 6;

type ScrolljackSectionProps = {
    blockScrolljackSection: TypeBlockScrolljackSection<'WITHOUT_UNRESOLVABLE_LINKS'>;
    numberOfItems?: number;
    mode?: 'dark' | 'light';
};

const formatNumber = (n: number) => String(n).padStart(2, '0');

export const ScrolljackSection = (props: ScrolljackSectionProps) => {
    const { blockScrolljackSection, numberOfItems, mode = 'dark' } = props;
    const updatedBlock = useContentfulLiveUpdates(blockScrolljackSection);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock?.sys?.id });
    const { fields } = updatedBlock;
    const sectionRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const prefersReducedMotion = useReducedMotion();
    const shouldReduceMotion = prefersReducedMotion === true;

    if (!fields) return null;

    const { overline, headerText, paragraph, items } = fields;

    if (!items || items.length === 0) return null;

    const allValidItems = items.filter(
        (item): item is TypeScrolljackItem<'WITHOUT_UNRESOLVABLE_LINKS'> => !!item && !!item.fields
    );

    if (allValidItems.length < MIN_ITEMS) return null;

    const itemCount = numberOfItems ? Math.max(MIN_ITEMS, Math.min(MAX_ITEMS, numberOfItems)) : undefined;
    const validItems = itemCount ? allValidItems.slice(0, itemCount) : allValidItems;

    return (
        <LazyMotion features={loadFeatures}>
            <ScrolljackDesktop
                sectionRef={sectionRef}
                validItems={validItems}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                overline={overline}
                headerText={headerText}
                paragraph={paragraph}
                mode={mode}
                shouldReduceMotion={shouldReduceMotion}
                inspectorProps={inspectorProps}
            />
            {/* Mobile: single column — all items visible */}
            <section className="md:hidden bg-gray-900 py-20 px-6">
                <div className="w-full mx-auto max-w-container space-y-10">
                    <div className="space-y-6">
                        {overline && (
                            <h2 className="h5 overline text-white" {...inspectorProps({ fieldId: 'overline' })}>
                                {overline}
                            </h2>
                        )}
                        {headerText && (
                            <h3 className="h-xl text-white" {...inspectorProps({ fieldId: 'headerText' })}>
                                {headerText}
                            </h3>
                        )}
                        {paragraph && (
                            <TextMarkdown
                                className="p-xl text-white"
                                text={paragraph}
                                mode="dark"
                                {...inspectorProps({ fieldId: 'paragraph' })}
                            />
                        )}
                    </div>
                    <div className="space-y-12">
                        {validItems.map((item, index) => (
                            <ScrolljackItemContent key={item.sys.id} item={item} index={index} />
                        ))}
                    </div>
                </div>
            </section>
        </LazyMotion>
    );
};

type ScrolljackDesktopProps = {
    sectionRef: React.RefObject<HTMLDivElement | null>;
    validItems: TypeScrolljackItem<'WITHOUT_UNRESOLVABLE_LINKS'>[];
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    overline?: string;
    headerText?: string;
    paragraph?: string;
    mode: 'dark' | 'light';
    shouldReduceMotion: boolean;
    inspectorProps: ReturnType<typeof useContentfulInspectorMode>;
};

const ScrolljackDesktop = ({
    sectionRef,
    validItems,
    activeIndex,
    setActiveIndex,
    overline,
    headerText,
    paragraph,
    mode,
    shouldReduceMotion,
    inspectorProps,
}: ScrolljackDesktopProps) => {
    const itemCount = validItems.length;
    const isDark = mode === 'dark';

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end'],
    });

    const progressScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

    useMotionValueEvent(scrollYProgress, 'change', (latest) => {
        // Divide scroll range evenly among items
        const newIndex = Math.min(itemCount - 1, Math.floor(latest * itemCount));
        setActiveIndex(newIndex);
    });

    const activeItem = validItems[activeIndex];

    // Height: 100vh per item + extra for the last item to linger
    const sectionHeight = `${itemCount * 100 + 50}vh`;

    return (
        <section
            ref={sectionRef}
            className={classNames(`hidden md:block relative`, {
                'bg-gray-900 text-white': isDark,
                'bg-white text-gray-900': !isDark,
            })}
            style={{ height: sectionHeight }}
        >
            {/* Sticky inner container pinned to viewport */}
            <div className="sticky top-0 h-screen flex items-center px-[135px]">
                <div className="w-full mx-auto max-w-container">
                    <div className="flex flex-row gap-16">
                        {/* Left column */}
                        <div className="w-5/12 shrink-0 space-y-6">
                            {overline && (
                                <h2 className="h5 overline text-inherit" {...inspectorProps({ fieldId: 'overline' })}>
                                    {overline}
                                </h2>
                            )}
                            {headerText && (
                                <h3 className="h-xl" {...inspectorProps({ fieldId: 'headerText' })}>
                                    {headerText}
                                </h3>
                            )}
                            {paragraph && (
                                <TextMarkdown
                                    className="p-xl"
                                    text={paragraph}
                                    mode={isDark ? 'dark' : 'light'}
                                    {...inspectorProps({ fieldId: 'paragraph' })}
                                />
                            )}
                        </div>

                        {/* Progress line */}
                        <div className="relative w-0.5 shrink-0 self-stretch">
                            <div
                                className={classNames(`absolute inset-0`, {
                                    'bg-gray-800': isDark,
                                    'bg-gray-500': !isDark,
                                })}
                            />
                            {!shouldReduceMotion && (
                                <motion.div
                                    className={classNames(`absolute top-0 left-0 w-full h-full origin-top`, {
                                        'bg-white': isDark,
                                        'bg-gray-900': !isDark,
                                    })}
                                    style={{ scaleY: progressScaleY }}
                                />
                            )}
                        </div>

                        {/* Right column — one item at a time with crossfade */}
                        <div className="flex-1 flex items-center relative">
                            <AnimatePresence initial={false}>
                                <motion.div
                                    key={activeItem.sys.id}
                                    className="space-y-6 absolute inset-0 flex flex-col justify-center"
                                    initial={shouldReduceMotion ? false : { opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={shouldReduceMotion ? undefined : { opacity: 0, y: -30 }}
                                    transition={{ duration: 0.45, ease: 'easeInOut' }}
                                >
                                    <ScrolljackItemContent item={activeItem} index={activeIndex} />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

type ScrolljackItemContentProps = {
    item: TypeScrolljackItem<'WITHOUT_UNRESOLVABLE_LINKS'>;
    index: number;
};

const ScrolljackItemContent = ({ item, index }: ScrolljackItemContentProps) => {
    const updatedItem = useContentfulLiveUpdates(item);
    const itemInspectorProps = useContentfulInspectorMode({ entryId: updatedItem?.sys?.id });
    const { headline, bodyText } = updatedItem.fields;

    return (
        <div className="space-y-4">
            {headline && (
                <h4 className="h-md" {...itemInspectorProps({ fieldId: 'headline' })}>
                    <span className="text-gray-500">{formatNumber(index + 1)}</span> {headline}
                </h4>
            )}
            {bodyText && (
                <p className="p-xl text-inherit" {...itemInspectorProps({ fieldId: 'bodyText' })}>
                    {bodyText}
                </p>
            )}
        </div>
    );
};

export default ScrolljackSection;
